import { payloadCodeTypeEnum } from "../../plugins/opsctrl-foc/dataStruct";
import { parseCss } from "../../plugins/opsctrl-foc/functions";
import { newCrew, seatTaken, type CrewEntry } from "./boardbook-model";
import type { CrewOption } from "./crew-directory.service";

/** Roster display names use given-name first, or an explicit family-name comma. */
export function rosterName(name: string): { familyName: string; givenNames: string } {
  const cleaned = name.trim().replace(/\s+/g, " ");
  const comma = cleaned.indexOf(",");
  if (comma >= 0) return { familyName: cleaned.slice(0, comma).trim(), givenNames: cleaned.slice(comma + 1).trim() };
  const words = cleaned.split(" ");
  return words.length > 1
    ? { givenNames: words[0], familyName: words.slice(1).join(" ") }
    : { givenNames: "", familyName: cleaned };
}

/** Crew from flight loading only; the directory enriches names, never membership. */
export function timerCrew(
  occupants: { name: string; codeType: number; active: boolean; parameters: string; description?: string }[],
  directory: CrewOption[]
): CrewEntry[] {
  const result: CrewEntry[] = [];
  const normalize = (name: string) =>
    name.normalize("NFKC").replace(/,/g, " ").trim().replace(/\s+/g, " ").toLocaleLowerCase();
  const add = (name: string, role: CrewEntry["role"]) => {
    if (
      !name?.trim() ||
      result.some(
        (c) => normalize([c.givenNames, c.familyName].filter(Boolean).join(" ")) === normalize(name) && c.role === role
      )
    )
      return;
    const matches = directory.filter(
      (c) =>
        normalize(c.givenNames + " " + c.familyName) === normalize(name) ||
        normalize(c.familyName + " " + c.givenNames) === normalize(name)
    );
    const person = matches.length === 1 ? matches[0] : undefined;
    const names = person ?? rosterName(name);
    if (person && result.some((c) => c.role === role && c.externalId === person.externalId)) return;
    result.push({
      ...newCrew(role, result),
      familyName: names.familyName,
      givenNames: names.givenNames,
      externalId: person?.externalId ?? "",
    });
  };
  for (const person of occupants) {
    if (!person.active || !person.name?.trim()) continue;
    const duty = parseCss(person.parameters, "duty")?.toUpperCase();
    const signedRoles = (person.description || "").toUpperCase().split(/[,;\s]+/);
    const technicalDuty =
      ["HEMS-TC", "HMS-TC", "HEMS_TC", "TCHEMS", "HCM", "3"].includes(duty) ||
      signedRoles.some((role) => ["HEMS-TC", "HMS-TC", "TCHEMS", "HCM"].includes(role));
    const medicalSeat = /medical/i.test(parseCss(person.parameters, "positionName") ?? "");
    const matching = directory.filter(
      (c) =>
        normalize(c.givenNames + " " + c.familyName) === normalize(person.name) ||
        normalize(c.familyName + " " + c.givenNames) === normalize(person.name)
    );
    const medicalOccupant =
      medicalSeat &&
      matching.length === 1 &&
      matching[0].roles.some((role) => ["MEDIC", "MED", "DOC", "DOCTOR"].includes(role.trim().toUpperCase()));
    if (Number(person.codeType) === payloadCodeTypeEnum.pic) {
      add(person.name, "PIC");
    } else if (technicalDuty) {
      add(person.name, "TCHEMS");
    } else if (Number(person.codeType) === payloadCodeTypeEnum.fo) {
      add(person.name, "SIC");
    } else if (
      ["DOCTOR", "DOC"].includes(duty) ||
      (!duty &&
        medicalOccupant &&
        matching[0].roles.some((role) => ["DOC", "DOCTOR"].includes(role.trim().toUpperCase())))
    ) {
      add(person.name, "DOC");
    } else if (medicalOccupant || ["MED", "MEDIC", "4"].includes(duty)) {
      add(person.name, "MEDIC");
    } else if (
      Number(person.codeType) === payloadCodeTypeEnum.crew ||
      ["HEMS-TC", "HEMS_TC", "TCHEMS", "HCM", "3"].includes(duty)
    ) {
      add(person.name, "TCHEMS");
    }
  }
  return result;
}

/** Signed roles override matching people; a partial roster must not erase flight crew. */
export function mergeSignedCrew(flightCrew: CrewEntry[], signedCrew: CrewEntry[]): CrewEntry[] {
  const key = (person: CrewEntry) =>
    person.externalId ||
    [person.givenNames, person.familyName]
      .join(" ")
      .normalize("NFKC")
      .replace(/,/g, " ")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  const signed = new Set(signedCrew.map(key));
  const merged = [...signedCrew];
  for (const person of flightCrew) {
    if (signed.has(key(person))) continue;
    merged.push(seatTaken(person.seat, merged) ? { ...person, seat: "" } : person);
  }
  return merged;
}

export interface FlightLoadingReader {
  namedQuery(name: string, parameters: Record<string, unknown>): Promise<any[]>;
}

/** Loading missions are alternative configurations, so never sum their occupants. */
export async function passengersFromFlightLoading(
  flightId: string,
  database: FlightLoadingReader,
  readLoading: (mission: number, codeType: payloadCodeTypeEnum) => Promise<{ weightValue: number; active?: boolean }[]>
): Promise<number | null> {
  const loading = await database.namedQuery("annunciatorsLoadingByFlightId", { flightId, unsetState: 0 });
  const missions = [...new Set(loading.map((row) => Number(row.opsMissionType)).filter(Number.isFinite))];
  if (!missions.length) return null;
  const counts = await Promise.all(
    missions.map(
      async (mission) =>
        (await readLoading(mission, payloadCodeTypeEnum.pax)).filter(
          (row) => row.active !== false && row.weightValue > 0
        ).length
    )
  );
  return Math.max(...counts);
}

/** Read current (non-obsolete) loading variants, not the roster or duty signature. */
export async function crewFromFlightLoading(
  flightId: string,
  database: FlightLoadingReader,
  readLoading: (
    mission: number,
    codeType: payloadCodeTypeEnum
  ) => Promise<{ name: string; weightValue: number; parameters?: string; active?: boolean; description?: string }[]>,
  directory: CrewOption[],
  withoutLoading: () => Promise<CrewEntry[]> = async () => []
): Promise<CrewEntry[]> {
  const loading = await database.namedQuery("annunciatorsLoadingByFlightId", { flightId, unsetState: 0 });
  if (loading.length === 0) return withoutLoading();
  const missions = [...new Set(loading.map((row) => Number(row.opsMissionType)).filter(Number.isFinite))];
  const types = [payloadCodeTypeEnum.pic, payloadCodeTypeEnum.fo, payloadCodeTypeEnum.crew, payloadCodeTypeEnum.pax];
  const groups = await Promise.all(
    missions.flatMap((mission) =>
      types.map(async (codeType) => {
        const rows = await readLoading(mission, codeType);
        return rows
          .filter(
            (row) =>
              row.active !== false &&
              (row.weightValue > 0 || (codeType !== payloadCodeTypeEnum.pax && row.active === true))
          )
          .map((row) => ({
            name: row.name,
            codeType,
            active: true,
            parameters: row.parameters ?? "",
            description: row.description,
          }));
      })
    )
  );
  return timerCrew(groups.flat(), directory);
}

/** Signed duty membership is a fallback only when the flight has no loading. */
export async function crewFromSignedDuty(
  duty: { signature?: string; crewDesignator?: string; pilotInCommand?: string } | undefined,
  readDuty: (id: string) => Promise<{ name: string; codeType: number; active: boolean; parameters: string }[]>,
  directory: CrewOption[],
  readRoster: () => Promise<{ dutyId: string; dutyDesig: string }[]>
): Promise<CrewEntry[]> {
  if (!duty?.signature || !duty.crewDesignator) return [];
  // flightCrewId identifies the signature, not the roster's dutyId.
  const roster = (await readRoster()).filter((row) => row.dutyDesig === duty.crewDesignator).pop();
  if (!roster || roster.dutyId == null || String(roster.dutyId).trim() === "") return [];
  const occupants = await readDuty(String(roster.dutyId));
  const crew = timerCrew(occupants, directory);
  if (!crew.some((person) => person.role === "PIC") && duty.pilotInCommand?.trim()) {
    crew.unshift(
      ...timerCrew(
        [{ name: duty.pilotInCommand, codeType: payloadCodeTypeEnum.pic, active: true, parameters: "" }],
        directory
      )
    );
  }
  return crew;
}
